import { BigLoading } from "@/components/shared/loading";
import { usePathname } from "next/navigation";
import { createContext, useContext, useEffect, useState } from "react";

type UIState = {
  // States
  loading: boolean;
  submitting: boolean;
  fetching: boolean;
  error: string | null;

  // Actions
  setLoading: (val: boolean) => void;
  setSubmitting: (val: boolean) => void;
  setFetching: (val: boolean) => void;
  setError: (msg: string | null) => void;
};

const UIStateContext = createContext<UIState | undefined>(undefined);

export default function UIStateProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pathname = usePathname();

  const clearStates = () => {
    setLoading(false);
    setSubmitting(false);
    setFetching(false);
    setError(null);
  };

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 2000);
    return () => clearTimeout(timer);
  }, [pathname]);

  if (loading) return <BigLoading />;

  const value = {
    loading,
    submitting,
    fetching,
    error,

    setLoading,
    setSubmitting,
    setFetching,
    setError,

    clearStates,
  };

  return (
    <UIStateContext.Provider value={value}>{children}</UIStateContext.Provider>
  );
}

export function useUIState() {
  const ctx = useContext(UIStateContext);
  if (!ctx) throw new Error("useUIState must be used within a UIStateProvider");
  return ctx;
}
